/**
 * IndustryEVComparisonChart
 *
 * Stacked area chart showing Enterprise Value per company over time.
 */
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear, formatCurrencyValue } from '../../financial/utils/formatters';
import type { RegionConfig } from '../../../types/financial';
import type { CompanyTimeSeries } from '../../../types/industryResearch';

const COMPANY_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#f97316', '#84cc16',
  '#ec4899', '#14b8a6',
];

interface Props {
  companies: CompanyTimeSeries[];
  region?: RegionConfig;
}

export default function IndustryEVComparisonChart({ companies, region }: Props) {
  const yearSet = new Set<number>();
  companies.forEach((co) => co.data.forEach((r) => yearSet.add(r.year)));
  const years = Array.from(yearSet).sort((a, b) => a - b);

  const chartData = years.map((yr) => {
    const entry: Record<string, number | string | null> = {
      year: formatFiscalYear(yr, region),
    };
    companies.forEach((co) => {
      const rec = co.data.find((d) => d.year === yr);
      entry[co.company_name] = rec?.enterprise_value ?? null;
    });
    return entry;
  });

  const unit = region?.currency?.unit_abbr ?? '';

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold text-neutral-800">Enterprise Value by Company ({unit})</h3>
        <p className="text-xs text-neutral-500 mt-0.5">EV trend per company over time</p>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
            <defs>
              {companies.map((co, i) => (
                <linearGradient key={co.company_name} id={`ev-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COMPANY_COLORS[i % COMPANY_COLORS.length]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COMPANY_COLORS[i % COMPANY_COLORS.length]} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis
              width={64}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatCurrencyValue(v, region, true)}
            />
            <Tooltip
              formatter={(value: number, name: string) => [formatCurrencyValue(value, region), name]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {companies.map((co, i) => (
              <Area
                key={co.company_name}
                type="monotone"
                dataKey={co.company_name}
                stroke={COMPANY_COLORS[i % COMPANY_COLORS.length]}
                strokeWidth={2}
                fill={`url(#ev-grad-${i})`}
                connectNulls
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
